import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useMemo } from 'react'
import environment from '../../../libs/environment'
import { usePaymentStore } from '../../../libs/hooks/usePaymentStore'
import { PaymentProviderType } from '@web/graphql-types'
import { useDonationFormContext } from '@web/components/page-components/DonationForm'
import { TEXT_COLOR_TO_HEX } from './utils'
import { useBrandingsStore } from '@web/components/utils/branding/useBrandingsStore'
import { PaymentMethodType } from '@web/graphql-components'

const stripePromiseCache = new Map<string, ReturnType<typeof loadStripe>>()

const stripePromise = (publishableKey: string, connectAccountId: string) => {
  const cacheKey = `${publishableKey}:${connectAccountId}`
  let cached = stripePromiseCache.get(cacheKey)
  if (!cached) {
    cached = loadStripe(
      publishableKey ?? environment.stripePublishableKey,
      connectAccountId ? { stripeAccount: connectAccountId } : undefined,
    )
    stripePromiseCache.set(cacheKey, cached)
  }
  return cached
}

const stripeAppearance = (primary: string) => ({
  appearance: {
    rules: {
      '.Label': {
        fontSize: '16px',
        fontWeight: '500',
        marginBottom: '8px',
        color: primary,
      },
      '.TermsText': {
        color: primary,
      },
    },
  },
})

const StripeWrapper = ({ children }: React.PropsWithChildren<{ paymentMethodType?: PaymentMethodType | null }>) => {
  const stripePaymentSettings = usePaymentStore(
    (state) => state.paymentSettings?.donationPagePaymentProviderSettings.stripePaymentSettings,
  )
  const clientSecret = usePaymentStore((store) => store.clientSecret)
  const settings = useDonationFormContext((state) => state.settings)
  const colors = useBrandingsStore((state) => state.colorData)
  const stripe = useMemo(() => {
    if (!stripePaymentSettings) {
      return null
    }

    const connectAccountId = stripePaymentSettings.paymentProviderType === PaymentProviderType.StripeStandard
      ? stripePaymentSettings.connectAccountId ?? ''
      : ''
    return stripePromise(stripePaymentSettings.publishableKey ?? '', connectAccountId)
  }, [stripePaymentSettings])
  const textColor = settings?.styles?.textColor?.includes('#')
    ? settings?.styles?.textColor
    : (colors?.[settings?.styles?.textColor] ?? '#000000')

  if (!stripePaymentSettings) {
    return null
  }
  const options = {
    ...(clientSecret ? { clientSecret } : {}),
    ...stripeAppearance(TEXT_COLOR_TO_HEX[textColor] ?? textColor ?? 'ge.text.primary'),
    paymentMethodCreation: 'manual',
  }
  return (
    <Elements options={options} stripe={stripe}>
      {children}
    </Elements>
  )
}

export default StripeWrapper
